import { useState, useEffect, useMemo } from "react";
import {
    TablePagination,
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Typography,
} from "@mui/material";

//Custom Components
import Table from "../../components/Table";
import ActionsMenu from "../../components/ActionsMenu";
import ContactFormModal from "./components/ContactFormModal";
import { ContactFormColumns } from './components/ContactFormColumns';
// Icons
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import api from "../../utils/api";


export default function ContactForms() {
    const columns = useMemo(() => ContactFormColumns(), []);

    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedContactFormId, setSelectedContactFormId] = useState(null);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;

        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;

        setLoading(true);

        try {
            const response = await api.get("admin/contact-form", {
                params: {
                    search: filters.search || search,
                    page: currentPage,
                    per_page: currentPerPage,
                },
            });

            setPagination({
                page: response.data.current_page || currentPage,
                perPage: response.data.per_page || currentPerPage,
                total: response.data.total || 0,
                totalPages: response.data.last_page || 0,
            });

            setRows(response.data.data || []);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRows = async (rows) => {
        try {
            for (const rowId of rows) {
                await api.delete(`admin/contact-form/${rowId}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.error("Contact form delete failed", err);
            window.alert(
                err?.response?.data?.message ||
                    (typeof err?.response?.data === "string" ? err.response.data : null) ||
                    "Could not delete contact form."
            );
        }
    };

    const handleView = (row) => {
        setSelectedContactFormId(row.id);
        setModalOpen(true);
    };

    const handleUpdate = () => {
        fetchRows(activeFilters); // Refresh the list after status update
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = { search };
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Contact Forms
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between" }}>
                    <Box>
                        <FormControl sx={{ width: { xs: '100%', md: '45ch' } }} variant="outlined">
                            <OutlinedInput
                                size="small"
                                id="search"
                                placeholder="Search by name, email, or message..."
                                sx={{ flexGrow: 1, background: "#fff" }}
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ color: 'text.primary' }}>
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                inputProps={{
                                    'aria-label': 'search',
                                }}
                            />
                        </FormControl>
                    </Box>
                </Box>

                <Table
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    selactable={false}
                    draggable={false}
                    renderActions={(row) => (
                        <>
                            <ActionsMenu
                                actions={[
                                    {
                                        label: "View",
                                        onClick: () => handleView(row),
                                    },
                                    {
                                        label: "Delete",
                                        onClick: () => handleDeleteRows([row.id]),
                                        confirm: true,
                                        confirmText: `Are you sure you want to delete this contact form?`,
                                    },
                                ]}
                            />
                        </>
                    )} />

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(event, newPage) => {
                        handlePageChange(newPage)
                    }}
                    rowsPerPageOptions={[]}
                />
            </Box>

            <ContactFormModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedContactFormId(null);
                    fetchRows(); // Refresh to update read status
                }}
                contactFormId={selectedContactFormId}
                onUpdate={handleUpdate}
            />
        </Box >
    );
}
